import { Injectable } from '@angular/core';
import { AuthService } from '../shared/authentication/auth.service';
import { FeatureToggleService } from '../shared/feature-toggle.service';
import { BehaviorSubject } from 'rxjs';
import { PermissionFilter } from '../shared/model';
import { capitalize } from '../shared/model/utility';
import { AlertFieldDefinition } from '../hickory-api/model/alertFieldDefinition';
import { AlertFieldDefinitionStatus } from '../hickory-api/model/alertFieldDefinitionStatus';
import { AlertsApi } from '../hickory-api';

export interface AlertFormType {
  alertType?: string;
  icon?: string;
}

@Injectable()
export class AlertFormService {
  alertFormTypes: AlertFormType[];
  ready: BehaviorSubject<boolean> = new BehaviorSubject(null);
  error: BehaviorSubject<any> = new BehaviorSubject(null);
  private alertFieldDefinitions: AlertFieldDefinition[];

  constructor(
    private authService: AuthService,
    private alertsApi: AlertsApi,
    public featureService: FeatureToggleService
  ) {
    this.fetchAll();

    this.alertFormTypes = [
      {
        alertType: 'Default',
        icon: 'sms_failed'
      },
      {
        alertType: 'Poll',
        icon: 'poll'
      }
    ];
  }

  fetchAll() {
    this.error.next(null);
    this.alertsApi.getAllAlertFieldDefinitons().subscribe({
      next: res => {
        this.alertFieldDefinitions = res?.items;
        this.ready.next(true);
      }, error: err => {
        this.error.next(err);
      }
    });
  }

  getAlertFormTypes(): AlertFormType[] {
    return this.alertFormTypes;
  }

  getFormType(type: string): AlertFormType {
    const formType = this.alertFormTypes.find(item => item.alertType === type);
    return formType ?? {
      alertType: type,
      icon: 'help'
    };
  }

  getAlertFormFieldDefinitions(): AlertFieldDefinition[] {
    if (!this.alertFieldDefinitions?.length) return [];
    // We need to filter out some fields based on permissions
    let fields = [...this.alertFieldDefinitions];
    const original = this.alertFieldDefinitions;
    
    original.forEach(field => {
      if (this.shouldFilter(field.adminPermissions)) {
        fields = fields.filter(f => f.id !== field.id);
      }
      if (this.shouldFilter(field.featureToggles)) {
        fields = fields.filter(f => f.id !== field.id);
      }
    });

    return fields;
  }

  /**
   * Helper function to process the permission and/or feature toggle requirements for each field
   */
   
  private shouldFilter(requirement: string): boolean {
    if (!requirement?.length) return false;

    const filter: PermissionFilter = { all: [], any: [], not: [] };
    const parts = requirement.split(',');
    parts.forEach(part => {
      part = part.trim();
      if (part.startsWith('!')) {
        filter.not.push(part.substring(1));
      } else {
        filter.all.push(part);
      }
    });

    return !this.authService.isSatisfied(filter);
  }

  getFieldById(id: string): AlertFieldDefinition {
    const definition = this.alertFieldDefinitions?.find(item => item.id === id);
    return definition ?? {
      id: id,
      name: capitalize(id),
      description: '',
      icon: 'help',
      position: 99,
      status: AlertFieldDefinitionStatus.Active,
      required: false,
      onAlertForm: true,
      onPollForm: true,
    };
  }
}
